import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiClient } from '../api/client';
import type { Me, TokenResponse } from '../api/types';
import { hasPermission, satisfies, type PermissionRequirement } from './permissions';

type Status = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: Status;
  me: Me | null;
  login(email: string, password: string): Promise<void>;
  /** Completes sign-in from a token response (e.g. invitation acceptance). */
  completeSignIn(token: TokenResponse): Promise<void>;
  logout(): Promise<void>;
  reloadMe(): Promise<Me | null>;
  switchContext(organizationId: string, schoolId: string | null): Promise<void>;
  can(permission: string, schoolId?: string | null): boolean;
  satisfies(requirement: PermissionRequirement | undefined): boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const CHANNEL = 'intellschools-auth';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const channel = useRef<BroadcastChannel | null>(null);

  const reloadMe = useCallback(async () => {
    const res = await apiClient.get<{ data: Me }>('/me');
    setMe(res.data);
    setStatus('authenticated');
    return res.data;
  }, []);

  const signOutLocally = useCallback(() => {
    apiClient.setSession(null);
    setMe(null);
    setStatus('anonymous');
  }, []);

  // Restore the session from the refresh cookie on first load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await apiClient.refresh();
      if (cancelled) return;
      if (!ok) {
        setStatus('anonymous');
        return;
      }
      try {
        await reloadMe();
      } catch {
        if (!cancelled) signOutLocally();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadMe, signOutLocally]);

  // Session expiry anywhere in the app, and sign-out/context changes in other tabs.
  useEffect(() => {
    const unsubscribe = apiClient.onSessionExpired(signOutLocally);
    if (typeof BroadcastChannel !== 'undefined') {
      channel.current = new BroadcastChannel(CHANNEL);
      channel.current.onmessage = (event: MessageEvent<{ type: string }>) => {
        if (event.data?.type === 'logout') signOutLocally();
        if (event.data?.type === 'context') void reloadMe().catch(signOutLocally);
      };
    }
    return () => {
      unsubscribe();
      channel.current?.close();
      channel.current = null;
    };
  }, [reloadMe, signOutLocally]);

  const completeSignIn = useCallback(
    async (token: TokenResponse) => {
      apiClient.setSession(token);
      await reloadMe();
    },
    [reloadMe],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await apiClient.post<{ data: TokenResponse }>(
        '/auth/login',
        { email, password },
        { anonymous: true },
      );
      await completeSignIn(res.data);
    },
    [completeSignIn],
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.postWithCsrf('/auth/logout');
    } finally {
      signOutLocally();
      channel.current?.postMessage({ type: 'logout' });
    }
  }, [signOutLocally]);

  const switchContext = useCallback(async (organizationId: string, schoolId: string | null) => {
    const res = await apiClient.put<{ data: Me }>('/me/context', { organizationId, schoolId });
    setMe(res.data);
    channel.current?.postMessage({ type: 'context' });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      login,
      completeSignIn,
      logout,
      reloadMe,
      switchContext,
      can: (permission, schoolId) => hasPermission(me?.context.permissions, permission, schoolId),
      satisfies: (requirement) =>
        satisfies(me?.context.permissions, requirement, me?.user.isPlatformAdmin ?? false),
    }),
    [status, me, login, completeSignIn, logout, reloadMe, switchContext],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
